import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useWhiteLabel } from '../contexts/WhiteLabelContext';
import { agenciesAPI } from '../services/api';
import { Card, Button } from '../components/ui';
import Loading from '../components/common/Loading';
import { handleApiError, showToast, formatDate } from '../utils/helpers';
import {
  CreditCard,
  Building2,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Upload,
  FileText,
} from 'lucide-react';

const PaymentPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAdmin } = useAuth();
  const { isRTL, t } = useLanguage();
  const { agency } = useWhiteLabel();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [agencyData, setAgencyData] = useState(null);
  const [paymentData, setPaymentData] = useState({
    bank_name: '',
    account_number: '',
    iban: '',
    reference_number: '',
    receipt_file: null,
    receipt_preview: null,
    notes: '',
  });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchAgencyData();
  }, [isAdmin, navigate]);

  const fetchAgencyData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await agenciesAPI.getMyAgency();
      setAgencyData(response.data.agency);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && !file.type.includes('pdf')) {
      setError(isRTL ? 'الملف يجب أن يكون صورة أو PDF' : 'File must be an image or PDF');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError(isRTL ? 'حجم الملف يجب أن يكون أقل من 10 ميجابايت' : 'File size must be less than 10MB');
      return;
    }

    setPaymentData(prev => ({
      ...prev,
      receipt_file: file,
      receipt_preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }));
    setError(null);
  };

  const handleRemoveFile = () => {
    setPaymentData(prev => ({
      ...prev,
      receipt_file: null,
      receipt_preview: null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      // TODO: Create payment submission API endpoint
      // For now, just show success message
      showToast.success(
        isRTL 
          ? 'تم إرسال معلومات الدفع بنجاح! سيتم مراجعة طلبك قريباً.'
          : 'Payment information submitted successfully! Your request will be reviewed shortly.'
      );
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      setError(handleApiError(err));
      showToast.error(handleApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (!agencyData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">
          {isRTL ? 'لا يمكن تحميل معلومات الوكالة' : 'Unable to load agency information'}
        </p>
      </div>
    );
  }

  const selectedPlan = agencyData.plan_name || (isRTL ? 'غير محدد' : 'Not set');
  const planPrice = agencyData.plan_price || 0;
  const subscriptionStatus = agencyData.subscription_status;

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <Card className="p-6 bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-slate-200">
          <div className="flex items-center gap-4">
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <div className="flex items-center gap-2 mb-1">
                <CreditCard className="w-5 h-5 text-sky-600" />
                <span className="text-xs font-semibold uppercase tracking-wide text-sky-600">
                  {isRTL ? 'الدفع والاشتراك' : 'Payment & Subscription'}
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">
                {isRTL ? 'إتمام الدفع' : 'Complete Payment'}
              </h1>
              <p className="text-slate-600 text-sm mt-1">
                {isRTL 
                  ? 'أكمل عملية الدفع لتفعيل اشتراكك'
                  : 'Complete payment to activate your subscription'}
              </p>
            </div>
          </div>
        </Card>

        {/* Plan Summary */}
        <Card className="p-6 border-slate-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-sky-100 rounded-lg">
              <Building2 className="w-6 h-6 text-sky-600" />
            </div>
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h2 className="text-lg font-semibold text-slate-900">
                {isRTL ? 'ملخص الاشتراك' : 'Subscription Summary'}
              </h2>
              <p className="text-sm text-slate-600">
                {agencyData.name}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-200">
            <div>
              <p className="text-sm text-slate-600 mb-1">
                {isRTL ? 'الباقة المختارة' : 'Selected Plan'}
              </p>
              <p className="font-semibold text-slate-900">{selectedPlan}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 mb-1">
                {isRTL ? 'المبلغ المطلوب' : 'Amount Due'}
              </p>
              <p className="font-semibold text-slate-900 text-lg">
                ${planPrice.toFixed(2)} / {isRTL ? 'شهر' : 'month'}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-600 mb-1">
                {isRTL ? 'حالة الاشتراك' : 'Subscription Status'}
              </p>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                subscriptionStatus === 'active'
                  ? 'bg-green-100 text-green-800'
                  : subscriptionStatus === 'trial'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {subscriptionStatus === 'active'
                  ? (isRTL ? 'نشط' : 'Active')
                  : subscriptionStatus === 'trial'
                  ? (isRTL ? 'تجريبي' : 'Trial')
                  : (isRTL ? 'منتهي' : 'Expired')}
              </span>
            </div>
            {agencyData.subscription_end_date && (
              <div>
                <p className="text-sm text-slate-600 mb-1">
                  {isRTL ? 'تاريخ الانتهاء' : 'End Date'}
                </p>
                <p className="font-semibold text-slate-900">
                  {formatDate(agencyData.subscription_end_date, isRTL)}
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Payment Instructions */}
        <Card className="p-6 border-slate-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">
              {isRTL ? 'تعليمات الدفع' : 'Payment Instructions'}
            </h2>
          </div>

          <div className="space-y-4 text-sm text-slate-700">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="font-semibold mb-2">
                {isRTL ? 'طريقة الدفع: تحويل بنكي' : 'Payment Method: Bank Transfer'}
              </p>
              <div className="space-y-2">
                <p>
                  <span className="font-medium">{isRTL ? 'اسم البنك:' : 'Bank Name:'}</span>{' '}
                  <span className="text-slate-600">
                    {isRTL ? 'يرجى إدخال اسم البنك في النموذج أدناه' : 'Please enter bank name in the form below'}
                  </span>
                </p>
                <p>
                  <span className="font-medium">{isRTL ? 'رقم الحساب:' : 'Account Number:'}</span>{' '}
                  <span className="text-slate-600">
                    {isRTL ? 'يرجى إدخال رقم الحساب في النموذج أدناه' : 'Please enter account number in the form below'}
                  </span>
                </p>
                <p>
                  <span className="font-medium">{isRTL ? 'IBAN:' : 'IBAN:'}</span>{' '}
                  <span className="text-slate-600">
                    {isRTL ? 'يرجى إدخال IBAN في النموذج أدناه' : 'Please enter IBAN in the form below'}
                  </span>
                </p>
              </div>
            </div>

            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-900 mb-1">
                    {isRTL ? 'مهم:' : 'Important:'}
                  </p>
                  <p className="text-amber-800">
                    {isRTL 
                      ? 'بعد إتمام التحويل البنكي، يرجى رفع إيصال التحويل أو إدخال رقم المرجع في النموذج أدناه. سيتم مراجعة طلبك وتفعيل الاشتراك خلال 24-48 ساعة.'
                      : 'After completing the bank transfer, please upload the transfer receipt or enter the reference number in the form below. Your request will be reviewed and subscription activated within 24-48 hours.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Payment Form */}
        <Card className="p-6 border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900 mb-6">
            {isRTL ? 'معلومات الدفع' : 'Payment Information'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Bank Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="bank_name" className="block text-sm font-medium text-slate-700 mb-2">
                  {isRTL ? 'اسم البنك' : 'Bank Name'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="bank_name"
                  name="bank_name"
                  required
                  value={paymentData.bank_name}
                  onChange={handleChange}
                  placeholder={isRTL ? 'مثال: البنك الأهلي' : 'e.g., National Bank'}
                  className="input w-full"
                />
              </div>

              <div>
                <label htmlFor="account_number" className="block text-sm font-medium text-slate-700 mb-2">
                  {isRTL ? 'رقم الحساب' : 'Account Number'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="account_number"
                  name="account_number"
                  required
                  value={paymentData.account_number}
                  onChange={handleChange}
                  placeholder={isRTL ? 'مثال: 1234567890' : 'e.g., 1234567890'}
                  className="input w-full"
                />
              </div>
            </div>

            <div>
              <label htmlFor="iban" className="block text-sm font-medium text-slate-700 mb-2">
                {isRTL ? 'IBAN' : 'IBAN'} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="iban"
                name="iban"
                required
                value={paymentData.iban}
                onChange={handleChange}
                placeholder={isRTL ? 'مثال: SA1234567890123456789012' : 'e.g., SA1234567890123456789012'}
                className="input w-full"
              />
            </div>

            <div>
              <label htmlFor="reference_number" className="block text-sm font-medium text-slate-700 mb-2">
                {isRTL ? 'رقم المرجع (إن وجد)' : 'Reference Number (if any)'}
              </label>
              <input
                type="text"
                id="reference_number"
                name="reference_number"
                value={paymentData.reference_number}
                onChange={handleChange}
                placeholder={isRTL ? 'رقم المرجع من إيصال التحويل' : 'Reference number from transfer receipt'}
                className="input w-full"
              />
            </div>

            {/* Receipt Upload */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {isRTL ? 'إيصال التحويل' : 'Transfer Receipt'} (اختياري / Optional)
              </label>
              {paymentData.receipt_preview ? (
                <div className="space-y-2">
                  <div className="relative inline-block">
                    <img
                      src={paymentData.receipt_preview}
                      alt={isRTL ? 'معاينة الإيصال' : 'Receipt preview'}
                      className="w-48 h-48 object-contain border-2 border-slate-200 rounded-lg bg-slate-50 p-2"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveFile}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      ×
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">
                    {paymentData.receipt_file?.name}
                  </p>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-sky-500 transition-colors">
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">
                      {isRTL ? 'اضغط لرفع إيصال التحويل' : 'Click to upload transfer receipt'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {isRTL ? 'JPG, PNG, PDF (حد أقصى 10 ميجابايت)' : 'JPG, PNG, PDF (max 10MB)'}
                    </p>
                  </div>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-slate-700 mb-2">
                {isRTL ? 'ملاحظات إضافية' : 'Additional Notes'}
              </label>
              <textarea
                id="notes"
                name="notes"
                rows="3"
                value={paymentData.notes}
                onChange={handleChange}
                placeholder={isRTL ? 'أي ملاحظات إضافية...' : 'Any additional notes...'}
                className="textarea w-full"
              />
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
              <Link to="/dashboard">
                <Button type="button" variant="secondary" disabled={submitting}>
                  {isRTL ? 'إلغاء' : 'Cancel'}
                </Button>
              </Link>
              <Button type="submit" variant="primary" loading={submitting} disabled={submitting}>
                {isRTL ? 'إرسال معلومات الدفع' : 'Submit Payment Information'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default PaymentPage;
